import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';

import { INITIAL_DEMO_GOALS } from '../lib/demoData';

const GoalContext = createContext();

export const useGoals = () => {
  const context = useContext(GoalContext);
  if (!context) {
    throw new Error('useGoals must be used within a GoalProvider');
  }
  return context;
};

const DEMO_GOALS_KEY = 'spend_easy_demo_goals';

const loadDemoGoals = () => {
  try {
    const saved = localStorage.getItem(DEMO_GOALS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map(g => ({
        ...g,
        targetDate: g.targetDate ? new Date(g.targetDate) : null,
        createdAt: g.createdAt ? new Date(g.createdAt) : new Date()
      }));
    }
  } catch (e) {
    console.error('Error loading demo goals:', e);
  }
  return INITIAL_DEMO_GOALS;
};

const saveDemoGoals = (g) => {
  try {
    localStorage.setItem(DEMO_GOALS_KEY, JSON.stringify(g));
  } catch (e) {
    console.error('Error saving demo goals:', e);
  }
};

export const GoalProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setGoals([]);
      setLoading(false);
      return;
    }

    if (currentUser.isGuest) {
      const updateGuestData = () => {
        setGoals(loadDemoGoals());
      };
      updateGuestData();
      setLoading(false);
      window.addEventListener('spend_easy_demo_data_updated', updateGuestData);
      return () => {
        window.removeEventListener('spend_easy_demo_data_updated', updateGuestData);
      };
    }

    const goalsRef = collection(db, 'users', currentUser.uid, 'goals');
    const q = query(goalsRef, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const goalsList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        targetDate: docSnap.data().targetDate?.toDate?.() || null,
        createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
      }));
      setGoals(goalsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching goals:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const addGoal = async (goalData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const newGoal = {
        ...goalData,
        id: `demo-goal-${Date.now()}`,
        userId: currentUser.uid,
        currentAmount: goalData.currentAmount || 0,
        isCompleted: false,
        createdAt: new Date()
      };
      const updated = [newGoal, ...goals];
      setGoals(updated);
      saveDemoGoals(updated);
      return { id: newGoal.id };
    }

    const goalsRef = collection(db, 'users', currentUser.uid, 'goals');
    return addDoc(goalsRef, {
      ...goalData,
      userId: currentUser.uid,
      currentAmount: goalData.currentAmount || 0,
      isCompleted: false,
      createdAt: serverTimestamp()
    });
  };

  const updateGoal = async (goalId, goalData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = goals.map(g => {
        if (g.id === goalId) {
          return { ...g, ...goalData, updatedAt: new Date() };
        }
        return g;
      });
      setGoals(updated);
      saveDemoGoals(updated);
      return;
    }

    const goalRef = doc(db, 'users', currentUser.uid, 'goals', goalId);
    await updateDoc(goalRef, { ...goalData, updatedAt: serverTimestamp() });
  };

  const deleteGoal = async (goalId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = goals.filter(g => g.id !== goalId);
      setGoals(updated);
      saveDemoGoals(updated);
      return;
    }

    await deleteDoc(doc(db, 'users', currentUser.uid, 'goals', goalId));
  };

  const markGoalComplete = async (goalId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = goals.map(g => {
        if (g.id === goalId) {
          return { ...g, isCompleted: true, completedAt: new Date() };
        }
        return g;
      });
      setGoals(updated);
      saveDemoGoals(updated);
      return;
    }

    const goalRef = doc(db, 'users', currentUser.uid, 'goals', goalId);
    await updateDoc(goalRef, { isCompleted: true, completedAt: serverTimestamp() });
  };

  const updateGoalProgress = async (goalId, amount) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = goals.map(g => {
        if (g.id === goalId) {
          return { ...g, currentAmount: amount, updatedAt: new Date() };
        }
        return g;
      });
      setGoals(updated);
      saveDemoGoals(updated);
      return;
    }

    const goalRef = doc(db, 'users', currentUser.uid, 'goals', goalId);
    await updateDoc(goalRef, { currentAmount: amount, updatedAt: serverTimestamp() });
  };

  const value = {
    goals,
    loading,
    addGoal,
    updateGoal,
    deleteGoal,
    markGoalComplete,
    updateGoalProgress
  };

  return (
    <GoalContext.Provider value={value}>
      {children}
    </GoalContext.Provider>
  );
};
